import React, { useState } from "react";
import { motion } from "@/lib/framer-motion-shim";
import { Wrench, Phone, MapPin, Clock, CheckCircle, Car, Settings, Shield, Calendar, ChevronRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import BookingModal from "@/components/demos/BookingModal";
import ContactForm from "@/components/demos/ContactForm";
import DemoTopBar from "@/components/demos/DemoTopBar";
import Seo from "@/components/Seo";

const images = [
  "https://images.unsplash.com/photo-1691840204491-f5c608613b68?w=800&q=80",
  "https://images.unsplash.com/photo-1545732870-5dced7323d26?w=800&q=80",
  "https://images.unsplash.com/photo-1623221013483-1f3cbeffdcec?w=800&q=80",
  "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80",
];

const services = [
  { name: "Revisão Completa", price: "89€", description: "Inspeção de 40 pontos, troca de filtros e fluidos", icon: Settings },
  { name: "Mudança de Óleo", price: "45€", description: "Óleo sintético de alta qualidade incluído", icon: Car },
  { name: "Alinhamento & Balanceamento", price: "55€", description: "Geometria de direção e equilíbrio de rodas", icon: Settings },
  { name: "Diagnóstico Electrónico", price: "35€", description: "Leitura de códigos de erro e análise", icon: Shield },
  { name: "Travões (Substituição)", price: "120€", description: "Pastilhas ou discos, mão-de-obra incluída", icon: Shield },
  { name: "Ar Condicionado", price: "75€", description: "Recarga e verificação de fugas", icon: Settings },
];

const features = [
  "Técnicos certificados",
  "Peças originais e homologadas",
  "Garantia de 2 anos",
  "Orçamento gratuito",
  "Viatura de substituição",
  "Wi-Fi na sala de espera",
];

export default function MechanicDemo() {
  const location = useLocation();
  const [bookingOpen, setBookingOpen] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("agendar") === "1") {
      setBookingOpen(true);
    }
  }, [location.search]);

  return (
    <>
      <Seo
        title="Demo Oficina Mecânica | Web & Studio"
        description="Template demo para oficina mecânica com serviços, diferenciais e pedido de orçamento."
        path="/demo/mechanic"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen bg-slate-900 text-slate-50 theme-mechanic"
      >
        <DemoTopBar />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Grid background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }} />
        </div>

        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-600 rounded">
                  <Wrench className="w-5 h-5" />
                </div>
                <span className="text-blue-400 uppercase tracking-wider text-sm">OFICINA MECÂNICA</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
                AutoPro<br />
                <span className="text-blue-500">Garage</span>
              </h1>
              <p className="text-slate-400 text-lg leading-relaxed mb-8 max-w-md">
                Serviço profissional de manutenção e reparação automóvel. 
                Tecnologia de ponta e técnicos certificados.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setBookingOpen(true)}
                  className="group flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200"
                  data-testid="hero-booking-btn"
                >
                  <Calendar className="w-5 h-5" />
                  Agendar Serviço
                </button>
                <a
                  href="tel:+351945678901"
                  className="flex items-center justify-center gap-2 px-8 py-4 border border-slate-700 text-slate-300 rounded hover:border-blue-600 hover:text-blue-400 transition-colors duration-200"
                >
                  <Phone className="w-5 h-5" />
                  +351 945 678 901
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="relative"
            >
              <img
                src={images[0]}
                alt="Mechanic"
                className="w-full aspect-[4/3] object-cover rounded-lg"
              />
              {/* Stats overlay */}
              <div className="absolute -bottom-6 left-6 right-6 grid grid-cols-3 gap-4">
                <div className="bg-slate-800 p-4 rounded border border-slate-700">
                  <p className="text-2xl font-bold text-blue-500">15+</p>
                  <p className="text-xs text-slate-400">Anos Exp.</p>
                </div>
                <div className="bg-slate-800 p-4 rounded border border-slate-700">
                  <p className="text-2xl font-bold text-blue-500">5000+</p>
                  <p className="text-xs text-slate-400">Clientes</p>
                </div>
                <div className="bg-slate-800 p-4 rounded border border-slate-700">
                  <p className="text-2xl font-bold text-blue-500">98%</p>
                  <p className="text-xs text-slate-400">Satisfação</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 lg:py-32 bg-slate-950">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-blue-500 uppercase tracking-wider text-sm">// SERVIÇOS</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4">O Que Fazemos</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setBookingOpen(true)}
                className="group p-6 bg-slate-900 border border-slate-800 rounded-lg hover:border-blue-600/50 transition-all duration-300 cursor-pointer"
                data-testid={`service-card-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-blue-600/10 rounded">
                    <service.icon className="w-6 h-6 text-blue-500" />
                  </div>
                  <span className="text-2xl font-bold text-blue-500">{service.price}</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{service.name}</h3>
                <p className="text-slate-400 text-sm mb-4">{service.description}</p>
                <div className="flex items-center gap-2 text-blue-500 group-hover:text-blue-400 transition-colors">
                  <span className="text-sm">Agendar</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-blue-500 uppercase tracking-wider text-sm">// POR QUE NÓS</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-8">
                Qualidade &<br />Confiança
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {images.slice(1).map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Workshop ${index + 1}`}
                  className={`w-full object-cover rounded-lg ${index === 0 ? 'aspect-[4/5] col-span-1' : 'aspect-square'}`}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-blue-600">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Precisa de Assistência?
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
              Ligue-nos ou agende online. Orçamento gratuito e sem compromisso.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setBookingOpen(true)}
                className="px-8 py-4 bg-white text-blue-600 font-semibold rounded hover:bg-blue-50 transition-colors duration-200"
                data-testid="cta-booking-btn"
              >
                Agendar Online
              </button>
              <a
                href="tel:+351945678901"
                className="px-8 py-4 border-2 border-white text-white font-semibold rounded hover:bg-white/10 transition-colors duration-200"
              >
                Ligar Agora
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-24 lg:py-32 bg-slate-950">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-blue-500 uppercase tracking-wider text-sm">// CONTACTO</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-8">Localização</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-600/10 rounded flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Morada</p>
                    <p className="text-lg">Zona Industrial, Lote 45 - Braga</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-600/10 rounded flex items-center justify-center">
                    <Phone className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Telefone</p>
                    <p className="text-lg text-blue-400">+351 945 678 901</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-600/10 rounded flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Horário</p>
                    <p className="text-lg">Seg-Sex: 8h-18h | Sáb: 8h-13h</p>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="aspect-video bg-slate-800 rounded-lg flex items-center justify-center border border-slate-700">
                <MapPin className="w-8 h-8 text-slate-600" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-900 p-8 rounded-lg border border-slate-800"
            >
              <h3 className="text-2xl font-semibold mb-6">Pedir Orçamento</h3>
              <ContactForm demoType="mechanic" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-800">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <Wrench className="w-5 h-5 text-blue-500" />
              <span>© 2024 AutoPro Garage</span>
            </div>
            <p className="text-slate-500 text-sm">Demo Template - Oficina Mecânica</p>
          </div>
        </div>
      </footer>

        <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} demoType="mechanic" />
      </motion.div>
    </>
  );
}




